import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNotification } from "hooks/useNotification";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  patchTransportSedelWaste,
  TransportSedelQueryKey,
  WasteDto,
} from "api/transportSedel.api";
import { UppdragQueryKey } from "api/uppdrag.api";
import { Uppdrag } from "types/uppdrag.type";
import { useWasteCodeDropDown } from "api/wasteCode.api";
import { ComboBoxItem } from "components/ComboBox";
import { isAvskiljare } from "types/separator/separator.type";
import { Waste } from "types/waste.type";
import { getSeparatorDetailedLabel } from "types/separator/separator.helpers";

const wasteSchema = z.object({
  wasteCodeUuid: z.string({ message: "Obligatoriskt fält" }).uuid(),
  weightPreliminary: z.string().optional(),
  weightFinal: z.string().optional(),
  quantity: z.string().optional(),
  wasteCodeQuery: z.string().nullable().optional(),
  wasteType: z.enum(["Weight", "Quantity"]),
  tomningSeparatorUuid: z.string().uuid().nullable().optional(),
  tomningType: z
    .enum(["Heltomning", "Toppsugning", "NotApplicable", "NotAssigned"])
    .optional(),
  selectedWasteCode: z.custom<ComboBoxItem>(),
  comment: z.string().optional(),
});
// .refine(
//   (data) => {
//     if (data.wasteType === "Weight") {
//       return !!data.weightPreliminary && Number(data.weightPreliminary) > 0;
//     }
//     return true;
//   },
//   {
//     message: "Vikt måste anges och vara större än 0",
//     path: ["weightPreliminary"],
//   },
// )
// .refine(
//   (data) => {
//     if (data.wasteType === "Quantity") {
//       return (
//         !!data.quantity &&
//         Number.isInteger(Number(data.quantity)) &&
//         Number(data.quantity) > 0
//       );
//     }
//     return true;
//   },
//   {
//     message: "Antal måste vara ett positivt heltal",
//     path: ["quantity"],
//   },
// )
export type TWasteSchema = z.infer<typeof wasteSchema>;

export const useUpdateWasteLogic = (
  transportSedel: { id: number },
  uppdrag: Uppdrag,
  waste: Waste,
  separatorUuid?: string,
  setTomningSeparatorUuid?: Dispatch<SetStateAction<string | undefined>>,
  isSetSeparatorDisabled?: boolean,
  onCreateSuccessCb?: () => void,
) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TWasteSchema>({
    resolver: zodResolver(wasteSchema),
    defaultValues: {
      selectedWasteCode: {
        id: "",
        name: "",
        description: "",
      },
      wasteType: "Weight",
      tomningType: "Heltomning",
      wasteCodeQuery: "",
      tomningSeparatorUuid: separatorUuid,
    },
  });

  const selectedSeparator = (uppdrag.facility.separatorList ?? []).find(
    (x) => x.uuid === form.watch("tomningSeparatorUuid"),
  );

  const isAvskiljareSelected =
    !!selectedSeparator && isAvskiljare(selectedSeparator);

  useEffect(() => {
    form.setValue("tomningSeparatorUuid", separatorUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [separatorUuid]);

  useEffect(() => {
    if (isModalOpen) {
      !!waste
        ? form.reset({
            wasteCodeUuid: waste.wasteCode.uuid,
            weightPreliminary: waste.weightPreliminary ?? undefined,
            weightFinal: waste.weightFinal ?? undefined,
            quantity: waste.quantity ?? undefined,
            wasteType: waste.wasteType,
            tomningSeparatorUuid: waste.tomning?.separator?.uuid,
            tomningType: waste.tomning?.type,
            selectedWasteCode: {
              id: waste.wasteCode.uuid,
              name: waste.wasteCode.codePretty,
              description: waste.wasteCode.description,
            },
            comment: waste.comment ?? undefined,
          })
        : form.reset();
    }
  }, [form, isModalOpen, waste]);

  const onModalClose = () => {
    setIsModalOpen(false);
  };

  const afterModalClose = useCallback(() => {
    form.reset({
      wasteCodeUuid: waste.wasteCode.uuid,
      weightPreliminary: waste.weightPreliminary ?? undefined,
      weightFinal: waste.weightFinal ?? undefined,
      quantity: waste.quantity ?? undefined,
      wasteType: waste.wasteType,
      tomningSeparatorUuid: waste.tomning?.separator?.uuid,
      tomningType: waste.tomning?.type,
      selectedWasteCode: {
        id: waste.wasteCode.uuid,
        name: waste.wasteCode.codePretty,
        description: waste.wasteCode.description,
      },
      comment: waste.comment ?? undefined,
    });
    if (setTomningSeparatorUuid) {
      setTomningSeparatorUuid(undefined);
    }
  }, [form, setTomningSeparatorUuid, waste]);

  // CREATE

  // PATCH
  const patchMutation = useMutation({
    ...patchTransportSedelWaste(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UppdragQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [TransportSedelQueryKey.GetList],
      });
      onModalClose();
    },
    onError: () => notification.error(),
  });

  const onSubmit = form.handleSubmit((data: TWasteSchema) => {
    // Validate tomningType when an avskiljare is selected
    if (isAvskiljareSelected && !data.tomningType) {
      form.setError("tomningType", {
        type: "manual",
        message: "Tömningstyp måste anges när avskiljare är vald",
      });
      return;
    }

    const payload: WasteDto = {
      wasteCodeUuid: data.selectedWasteCode.id,
      weightPreliminary:
        data.wasteType === "Weight" ? data.weightPreliminary : undefined,
      weightFinal: data.wasteType === "Weight" ? data.weightFinal : undefined,
      quantity: data.wasteType === "Quantity" ? data.quantity : undefined,
      wasteType: data.wasteType,
      comment: data.comment,
      tomningSeparatorUuid: data.tomningSeparatorUuid,
      tomningType: data.tomningSeparatorUuid ? data.tomningType : undefined,
    };
    patchMutation.mutate({
      payload,
      transportSedelId: transportSedel.id,
      wasteId: waste.id,
    });
  });

  const separatorDropdownItems = (uppdrag.facility.separatorList ?? [])
    .map((x) => ({ label: getSeparatorDetailedLabel(x), value: x.uuid }))
    .sort((a, b) => (a.label < b.label ? -1 : 1));

  // waste codes
  const wasteCodeQuery = form.watch("wasteCodeQuery") ?? undefined;
  const selectedWasteCode = form.watch("selectedWasteCode");
  const { data: wasteCodes } = useWasteCodeDropDown(wasteCodeQuery);
  const wasteCodeComboBoxItems: ComboBoxItem[] = (wasteCodes ?? []).map(
    (c) => ({
      id: c.uuid,
      name: c.codePretty,
      description: c.description,
    }),
  );

  const setWasteCodeQuery = useCallback(
    (v?: string) => form.setValue("wasteCodeQuery", v),
    [form],
  );

  const setSelectedWasteCode = useCallback(
    (wc: ComboBoxItem) => {
      form.setValue(
        "selectedWasteCode",
        wc ?? {
          id: "",
          name: "",
          description: "",
        },
      );
      form.setValue("wasteCodeUuid", wc?.id ?? undefined);
    },
    [form],
  );

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    form,
    onSubmit,
    onModalClose,
    afterModalClose,
    uppdrag,
    separatorDropdownItems,
    wasteCodeComboBoxItems,
    setWasteCodeQuery,
    selectedWasteCode,
    setSelectedWasteCode,
    isAvskiljareSelected,
    isPending: patchMutation.isPending,
    hideSeparator: !!separatorUuid,
    isSetSeparatorDisabled,
    selectedSeparator,
  };
};

export type UpdateWasteLogicRetVal = ReturnType<typeof useUpdateWasteLogic>;
